<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import {
		getAddonSupported,
		getAddonsSupported,
		proSensorAddonSupport,
		type AddonType,
	} from 'src/helpers/addons/addon_support_helpers';
	import { needsAltFirmware } from 'src/helpers/alternative_firmware_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	//Services
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Design system components
	import { Alert, Button, Collapse, Radio, Text } from '@Shelly/shelly-design-system';
	//Components
	import AddonFirmwareSwitchModal from './AddonFirmwareSwitchModal.svelte';
	//Types
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Device } from 'src/types/Device';

	export let device: Device, addonType: AddonType;

	const addonsSupported = getAddonsSupported(device?.type);
	const loraAddon = getAddonSupported(device, 'LoRa');
	const sensorAddon = getAddonSupported(device, 'sensor');
	const switchAddon = getAddonSupported(device, 'prooutput');

	const selectAddonType = async (type: AddonType) => {
		selectedAddonType = type;
		if (proSensorAddonSupport(device)) {
			needsFirmwareSwitch = needsAltFirmware(device, addonType, selectedAddonType);
		}
	};

	const addAddon = async (addon_type: string) => {
		waitForResponse = true;
		const params = {
			config: { device: { addon_type: addon_type || null } },
		};
		const res: SettingRequest = await gen2Request(device, 'sys.SetConfig', params);
		onSettingsResponse(res, device);
		waitForResponse = false;
	};

	let open = addonType !== '';
	let selectedAddonType: AddonType = addonType;
	let needsFirmwareSwitch = false;
	let waitForResponse = false;
</script>

{#if addonsSupported?.length === 1 && loraAddon?.min_ver && !loraAddon?.fwSupported}
	<Alert
		type="info"
		class="mb-s"
		heading={$format('alert_unsupported_addon', {
			default: 'Unsupported Add-on, please update firmware.',
		})}
		cta={{
			title: capFirstChar(
				$format('sub_settings_title_firmware_update', {
					default: 'Firmware upgrade',
				}),
			),
		}}
		on:cta_click={() => navigateToSetting(device.id, 'settings', 'firmware_version')}
	/>
{:else}
	<Collapse
		id="collapse-3qn6nvo"
		title={capFirstChar($format('title_manage_addon_feature', { default: 'Manage add-on feature' }))}
		expanded={!open}
		class="mb-s"
		on:click={() => {
			open = !open;
		}}
	>
		<Text class="mb-xs" styleType="body-small">
			{$format('text_note_manage_addons', {
				default:
					'Please note that updating the type or disabling the add-on will require a reboot for the changes to take effect.',
			})}
		</Text>
		<div class="mb-xs">
			<Radio
				selected={selectedAddonType}
				value=""
				size="m"
				class="d-inline-flex mr-xs"
				id="radio-2jcoiho"
				on:click={() => selectAddonType('')}
			>
				{capFirstChar(
					$format('none', {
						default: 'none',
					}),
				)}
			</Radio>
			{#if switchAddon}
				<Radio
					selected={selectedAddonType}
					value="prooutput"
					size="m"
					disabled={!switchAddon?.fwSupported}
					class="d-inline-flex mr-xs"
					id="radio-hfvvzjk"
					on:click={() => selectAddonType('prooutput')}
				>
					{$format('button_switch_addon', { default: 'Switch Add-on' })}
				</Radio>
			{/if}
			{#if sensorAddon}
				<Radio
					selected={selectedAddonType}
					value="sensor"
					size="m"
					disabled={!sensorAddon?.fwSupported}
					class="d-inline-flex mr-xs"
					id="radio-ut1r9pg"
					on:click={() => selectAddonType('sensor')}
				>
					{$format('button_sensor_addon', { default: 'Sensor Add-on' })}
				</Radio>
			{/if}
			{#if loraAddon}
				<Radio
					selected={selectedAddonType}
					value="LoRa"
					size="m"
					disabled={!loraAddon?.fwSupported}
					class="d-inline-flex"
					id="radio-5bcc6yl"
					on:click={() => selectAddonType('LoRa')}
				>
					{$format('button_lora_addon', { default: 'LoRa Add-on' })}
				</Radio>
			{/if}
		</div>
		{#if needsFirmwareSwitch}
			<AddonFirmwareSwitchModal {device} {selectedAddonType} />
		{/if}
		<Button
			size="m"
			class="width-full"
			loading={waitForResponse}
			disabled={waitForResponse || selectedAddonType === addonType || needsFirmwareSwitch}
			on:click={() => addAddon(selectedAddonType)}
			>{$format('button_save_and_reboot', {
				default: 'Save and reboot',
			})}</Button
		>
	</Collapse>
{/if}
